import { emitHintSignal } from '@flobi/asset-library/hints';
import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../chrome/icons';
import { IconButton, Spinner } from '../../ui/controls';
import { Popover } from '../../ui/popover';
import { RENDER_LIMITS } from '@flobi/artwork-core/render-contract';
import type { ExportScope } from '../useEditorCanvas';
import type { CloudFormat } from '../cloudExport';
import { usePress } from '../press';
import { rasterSizeFits } from '../rasterExportLimits';
import { VIDEO_EDGE } from '../videoExport';

export type ExportCommand = 'png' | 'svg' | 'slices' | CloudFormat;
/** How big a picture or video is made: a multiple of the artboard, or the document's own resolution. */
export interface ExportSize {
  readonly scale: number;
  /** Set when the scale is the document's resolution rather than a plain multiple. */
  readonly ppi?: number;
}
type Format = 'png' | 'jpg' | 'svg' | 'pdf' | 'mp4' | 'gif' | 'slices';
type Sized = 'png' | 'jpg' | 'mp4' | 'gif';
export interface ExportOptions {
  scope: ExportScope;
  scopes: readonly { id: ExportScope; label: string; detail: string }[];
  onScopeChange: (scope: ExportScope) => void;
  count: number;
  resolution: number;
  slices: number;
  /** Output bounds, including selection strokes and effects. */
  size: { width: number; height: number } | null;
  sizes?: readonly { width: number; height: number }[];
  activity?: string | null;
  error?: string | null;
  onExport: (command: ExportCommand, size: ExportSize) => void | Promise<boolean | void>;
  /** Stops the active local or online export. */
  onCancel?: () => void;
  /** The last online export, to download again without drawing it again ("PDF"). */
  again?: { readonly name: string; readonly busy?: boolean; readonly run: () => void } | null;
  /** The last online export, sent on to Drive (a folder) or Handoff (a new transfer); `logo` is an app's directory icon. */
  /** Where it can go is the kit's Send to list for the export's kind (cross-app pages, 29 Oct): `{ app, label }`. */
  send?: { readonly apps: readonly { readonly app: string; readonly label: string }[]; readonly busy: string | null; readonly logo: (slug: string) => string | null; readonly run: (app: string) => void } | null;
  /** The artboard as layers on a Flow board, a Layer node with every layer placed (cross-app pages, 29 Oct): no export needed. */
  flow?: { readonly label: string; readonly logo: string | null; readonly run: () => void } | null;
  /** The format this page is most likely wanted in (Jev, from a summary of the page): preselected while nobody has picked one. */
  suggest?: () => Promise<'png' | 'jpg' | 'svg' | 'pdf' | 'mp4' | 'gif' | null>;
}
const SIZED: readonly Format[] = ['png', 'jpg', 'mp4', 'gif'];
/** What each format starts at: a JPEG is usually wanted sharp, a video light. */
const FIRST_SCALE: Record<Sized, number> = { png: 1, jpg: 2, mp4: 1, gif: 1 };
const SCALES = [1, 2, 4] as const;
const FPS: Partial<Record<Format, number>> = { mp4: 30, gif: 15 };
/** What a selection can be written as. A PDF page and a video are an artboard's: they are not offered for layers. */
const LAYER_FORMATS: readonly Format[] = ['png', 'jpg', 'svg'];
const FORMATS: { id: Exclude<Format, 'slices'>; label: string; note: string; icon: IconName }[] = [
  { id: 'png', label: 'PNG', note: 'High quality', icon: 'format-image' },
  { id: 'jpg', label: 'JPEG', note: 'Smaller file', icon: 'format-image' },
  { id: 'svg', label: 'SVG', note: 'Editable vector', icon: 'format-vector' },
  // Not "Print": there is no bleed, crop marks or CMYK yet, so a print shop would be let down (ART-UX-20).
  { id: 'pdf', label: 'PDF', note: 'For sharing', icon: 'format-pdf' },
  { id: 'mp4', label: 'MP4', note: 'Video', icon: 'format-video' },
  { id: 'gif', label: 'GIF', note: 'Looping animation', icon: 'format-animation' },
];

/**
 * The sizes a format is offered at: 1×, then 2× and 4× when what they make can be made (a
 * video frame has a lower ceiling than a picture), then — for a PNG — the document's own
 * resolution when it is not one of those already. Left out, never greyed.
 */
export function offeredSizes(format: Format, size: { width: number; height: number } | null, resolution: number): (number | 'ppi')[] {
  // A video is drawn on this computer: its only bound is the graphics card's widest surface, and
  // whether the browser's encoder takes the size (asked when it starts, with the cloud behind it).
  const video = format === 'mp4' || format === 'gif';
  const fits = (scale: number) => !size || (video
    ? Math.ceil(size.width * scale) <= VIDEO_EDGE && Math.ceil(size.height * scale) <= VIDEO_EDGE
    : rasterSizeFits(size, scale) && Math.ceil(size.width * scale) * Math.ceil(size.height * scale) <= RENDER_LIMITS.maxPixels);
  const ppi = format === 'png' && ![72, 144, 288].includes(resolution) && fits(resolution / 72);
  return [...SCALES.filter(fits), ...(ppi ? ['ppi' as const] : [])];
}

export function exportSummary(format: Format, count: number, slices: number): string {
  if (format === 'slices') return `${slices} SVG ${slices === 1 ? 'file' : 'files'} · one per slice`;
  if (format === 'pdf') return count > 1 ? `1 PDF · ${count} pages` : '1 PDF file';
  return count > 1 ? `${count} files in a ZIP` : '1 file';
}

export function ExportPanel({ open, anchor, onClose, options, progress }: {
  open: boolean; anchor: HTMLElement | null; onClose: () => void;
  options: ExportOptions; progress: number | null;
}) {
  const body = useRef<HTMLDivElement>(null);
  const close = () => { onClose(); anchor?.focus({ preventScroll: true }); };
  useEffect(() => {
    if (open) body.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
    if (open) emitHintSignal('export.opened');
  }, [open]);
  const [picked, setPicked] = useState<Format>('png');
  // A format the person pressed stands; until then the page's likely format is preselected (options.suggest).
  const chose = useRef(false);
  const setFormat = (value: Format) => { chose.current = true; setPicked(value); };
  useEffect(() => {
    if (!open || chose.current || !options.suggest) return;
    let live = true;
    void options.suggest().then((value) => { if (live && value && !chose.current) setPicked(value); }).catch(() => {});
    return () => { live = false; };
    // Asked as the panel opens, not on every change to the options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const press = usePress();
  /**
   * An export made, as flags for the hints (lib/hints): every artboard or one, a selection, a
   * PDF from an RGB file, a video. `fileBoards` is only "one" or "two or more" (All is offered then).
   */
  const tell = (format: Format) => {
    const all = scope === 'all';
    const layers = scope === 'layers';
    emitHintSignal('export.done', { all, layers, fileBoards: scopes.some((item) => item.id === 'all') ? 2 : 1, pdf: format === 'pdf', cmyk: press !== null });
    if (all) emitHintSignal('export.all');
    if (layers) emitHintSignal('export.selection');
    if (format === 'mp4' || format === 'gif') emitHintSignal('export.video');
    // Exporting from a CMYK file: this person has set a file up for print (Document Settings is not ours to wire).
    if (press !== null) emitHintSignal('document.cmyk');
  };
  /** The size chosen for each format, kept while the panel is open: 'ppi' is the document's own resolution. */
  const [scales, setScales] = useState<Record<Sized, number | 'ppi'>>(FIRST_SCALE);
  const { scope, scopes, onScopeChange, count, resolution, slices, size, onExport } = options;
  /** The layers chosen, alone: fewer formats, all of them made here in the page. */
  const layers = scope === 'layers';
  const format: Format = layers && !LAYER_FORMATS.includes(picked) ? 'png' : picked;
  const activeScope = scopes.find(item => item.id === scope) ?? scopes[0];
  const formatName = FORMATS.find(item => item.id === format)?.label ?? 'SVG slices';
  const sized = SIZED.includes(format) ? (format as Sized) : null;
  const ppiScale = resolution / 72;
  const offered = offeredSizes(format, size, resolution).filter(one =>
    (options.sizes ?? []).every(bounds => offeredSizes(format, bounds, resolution).includes(one)));
  const busy = progress !== null || !!options.activity;
  const tooLarge = !!sized && offered.length === 0;
  const disabled = busy || tooLarge || (format === 'slices' && slices === 0);
  const wanted = sized ? scales[sized] : 1;
  const chosen = offered.includes(wanted) ? wanted : Math.max(...offered.filter((one): one is number => typeof one === 'number' && typeof wanted === 'number' && one <= wanted), 1);
  const exportSize: ExportSize = chosen === 'ppi' ? { scale: ppiScale, ppi: resolution } : { scale: chosen };
  const pixels = size ? `${Math.round(size.width * exportSize.scale)} × ${Math.round(size.height * exportSize.scale)} px` : null;
  const fps = FPS[format];
  const send = options.send;
  // Flow once: the artboard as layers, when that is offered, stands for the exported file.
  const apps = (send?.apps ?? []).filter(({ app }) => !(app === 'flow' && options.flow));
  // Drive first: its page lists folders and projects, a place to open the export in (30 Oct).
  const openWith = [...apps.filter(({ app }) => app === 'drive'), ...apps.filter(({ app }) => app !== 'drive')];
  const sendButton = ({ app, label }: { app: string; label: string }) => {
    const logo = send!.logo(app);
    return <button key={app} type="button" className="export-cancel" disabled={send!.busy !== null} aria-busy={send!.busy === app} onClick={() => send!.run(app)}>{logo ? <img src={logo} alt="" width={15} height={15} /> : <span className="export-app-box" aria-hidden="true" />}{label}{send!.busy === app ? <Spinner size={11} /> : null}</button>;
  };
  return <Popover open={open} anchor={anchor} onClose={close} align="end" width={328} label="Export artwork" ownsKeyboard className="export-panel">
    <header className="export-panel-heading"><strong>Export</strong><IconButton icon="close" label="Close export" onClick={close} /></header>
    <div ref={body} className="export-panel-body ui-scroll">
      <section className="export-scope">
        <h3>What</h3>
        <div className="export-scope-options" role="group" aria-label="What to export">
          {scopes.map(item => <button key={item.id} type="button" disabled={busy || format === 'slices'} aria-pressed={scope === item.id} onClick={() => onScopeChange(item.id)}>{item.label}</button>)}
        </div>
        <p className="export-scope-detail" title={format === 'slices' ? 'All slices on this page' : activeScope?.detail}>{format === 'slices' ? 'All slices on this page' : activeScope?.detail}</p>
      </section>
      <section className="export-formats">
        <h3>Format</h3>
        <div className="export-format-grid" role="group" aria-label="Export format">
          {FORMATS.filter(item => !layers || LAYER_FORMATS.includes(item.id)).map(item => <button key={item.id} type="button" disabled={busy} aria-pressed={format === item.id} onClick={() => setFormat(item.id)}>
            <span className="export-format-icon"><Icon name={item.icon} size={23} /></span><span><strong>{item.label}</strong><small>{item.note}</small></span>
            {format === item.id && <span className="export-format-check"><Icon name="check" size={11} /></span>}
          </button>)}
        </div>
        {slices > 0 && !layers && <button type="button" disabled={busy} className="export-slices" aria-pressed={format === 'slices'} onClick={() => setFormat('slices')}><Icon name="slice" size={18} /><span>SVG slices</span><span>{slices}</span>{format === 'slices' && <Icon name="check" size={12} />}</button>}
      </section>
      <section className="export-format-options">
        {sized ? <><h3>Size</h3><div className="export-scale" role="group" aria-label="Size">
          {offered.map((one) => <button key={one} type="button" disabled={busy} aria-pressed={chosen === one} onClick={() => setScales((was) => ({ ...was, [sized]: one }))}>{one === 'ppi' ? `${resolution} ppi` : `${one}×`}</button>)}
        </div>
        {/* What that makes, said once: the pixels, and for a video how smooth. */}
        {pixels || fps || (format === 'jpg' && layers) ? <p className="export-size-note">{[pixels, fps ? `${fps} fps` : null, format === 'jpg' && layers ? 'on white' : null].filter(Boolean).join(' · ')}</p> : null}
        </> : <p>{format === 'svg' ? 'Vector artwork you can edit and scale.' : format === 'slices' ? 'Each slice downloads as a separate SVG.' : 'Each artboard becomes a page in one PDF.'}</p>}
      </section>
    </div>
    <footer className="export-panel-footer">
      <div className="export-output"><span>{exportSummary(format, count, slices)}</span>{!layers && format === 'pdf' ? <small>Renders online</small> : !layers && (format === 'mp4' || format === 'gif') ? <small>Renders on this computer</small> : null}</div>
      {tooLarge && <p role="alert" className="export-progress">{format === 'mp4' || format === 'gif' ? 'This video is more than 16,384 px on a side, the most a graphics card draws at once. Reduce its dimensions before exporting.' : 'This output exceeds 40 megapixels or 16,384 px per side. Reduce its dimensions before exporting.'}</p>}
      {options.error && !busy && <p role="alert" className="export-progress">{options.error}</p>}
      {options.activity && <p role="status" className="export-progress">{options.activity} You can keep editing.</p>}
      <button type="button" className="export-submit" disabled={disabled} aria-busy={busy} onClick={async () => { if (disabled) return; const done = await onExport(format, exportSize); if (done !== false) { tell(format); close(); } }}>{busy ? <Spinner size={18} /> : <Icon name="export-download" size={18} />}{busy ? progress !== null ? `Exporting… ${progress}%` : 'Exporting…' : `Export ${formatName}`}</button>
      {/* An online export can be stopped: there was no way to, short of leaving the file (QA 2026-10-05, ART-EXP-15). */}
      {busy && options.onCancel ? <button type="button" className="export-cancel" onClick={options.onCancel}>Cancel export</button> : null}
      {/* The last online export, kept while the file is open: a blocked download is one press away (ART-UX-19). */}
      {!busy && options.again ? <button type="button" className="export-cancel" disabled={options.again.busy} aria-busy={options.again.busy} onClick={options.again.run}><Icon name="export-download" size={15} />Download {options.again.name} Again{options.again.busy ? <Spinner size={11} /> : null}</button> : null}
      {/* …and sent on without downloading it (cross-app pages, 29 Oct): its files are media assets already. Open with
          every app, Drive first (30 Oct). */}
      {!busy && (openWith.length || options.flow) ? <section className="export-send-group" aria-label="Open with"><h3>Open with</h3>
        {openWith.map(sendButton)}
        {options.flow ? <button type="button" className="export-cancel" onClick={() => { options.flow!.run(); close(); }}>{options.flow.logo ? <img src={options.flow.logo} alt="" width={15} height={15} /> : <span className="export-app-box" aria-hidden="true" />}{options.flow.label}</button> : null}
      </section> : null}
    </footer>
  </Popover>;
}
